/**
 * The Eight Sisters as the UI draws them — one source for thread-colour, name,
 * glyph and position on the Eightfold Star.
 *
 * Canon: `docs/story/narrative/storyline.md` § "The Eight Sisters (the faith)". The table
 * there is the authority for the ORDER (1–8) and the **thread-colour**; the
 * runes and their glyphs come from `tools/gen-rune-stones.ts`, which also emits
 * `public/ui/runes/rune-<id>.svg` expressly "for UI reuse (Eightfold-Star
 * paths)". This module is what makes that reuse possible without a fifth copy.
 *
 * ⚠️ TWO COLOURS CHANGED HERE, because the code disagreed with the bible.
 * Every prior `HUE_COLOR` map (ledger-panel, loom-panel, threads-of-fate,
 * anchor-glow — four copies, all agreeing with each other) had **Vef green and
 * Skugg violet**. Canon says Vefna the Weaver's thread is PURPLE and Skugga the
 * Veiled One's is FOREST-GREEN. They were swapped. Corrected here; the two 3D
 * VFX mirrors still hold the old pair and are Martin's call, since flipping them
 * changes spell glows in the world — see §78.
 *
 * The order is not decorative either. Sól sits at the crown and the ring runs
 * clockwise down the alignment ladder — LG, NG, CG, N, LN, LE, NE, CE — so the
 * three dark Sisters (Skugg, Hol, Vill) fall on the LEFT of the star. Sinister,
 * in the old sense.
 */
import type { RuneHue } from "../../types.js";

export interface RuneMeta {
  readonly hue: RuneHue;
  /** The rune's own name, as the world says it. */
  readonly label: string;
  /** The Sister whose craft it is. */
  readonly sister: string;
  /** Liturgical thread-colour, per the bible's table. */
  readonly color: string;
  /** Position on the Eightfold Star, in degrees clockwise from the crown. */
  readonly angle: number;
  /** Glyph, served from `public/ui/runes/` — used as a CSS mask so it tints. */
  readonly glyph: string;
}

/** The eight, in canon order: Sól at the crown, then clockwise. */
export const RUNES: readonly RuneMeta[] = Object.freeze([
  { hue: "sol", label: "Sól", sister: "Sóla", color: "#f2c85a", angle: 0, glyph: "sol" },
  { hue: "vef", label: "Vef", sister: "Vefna", color: "#a679d6", angle: 45, glyph: "vef" },
  { hue: "eld", label: "Eld", sister: "Eldra", color: "#ff8a3d", angle: 90, glyph: "eld" },
  { hue: "man", label: "Mán", sister: "Mána", color: "#5b76c4", angle: 135, glyph: "man" },
  { hue: "jarn", label: "Járn", sister: "Járna", color: "#b9bec7", angle: 180, glyph: "jarn" },
  { hue: "skugg", label: "Skugg", sister: "Skugga", color: "#4e7c52", angle: 225, glyph: "skugg" },
  { hue: "hol", label: "Hol", sister: "Hola", color: "#c3c9cd", angle: 270, glyph: "hol" },
  { hue: "vill", label: "Vill", sister: "Villa", color: "#c8384f", angle: 315, glyph: "vill" },
]);

/** Parchment ink — the colour of an unhued or unnamed thing. */
export const NO_HUE_COLOR = "#c9b58a";

const BY_HUE: ReadonlyMap<RuneHue, RuneMeta> = new Map(RUNES.map((r) => [r.hue, r] as const));

/** Thread-colour for a hue; parchment ink for `none` or anything unknown. */
export function hueColor(hue: RuneHue): string {
  return BY_HUE.get(hue)?.color ?? NO_HUE_COLOR;
}

/**
 * The rune's NAME for a hue — "Eld", "Vef" — or the raw hue string if it is not
 * one of the eight.
 *
 * This module already promises to be the one source for "thread-colour, name,
 * glyph and position"; the name half simply had no accessor, so callers reached
 * for `RUNES` and read `.label` themselves. That is fine when you are already
 * iterating the eight (the Ledger draws the whole star), and wrong when you have
 * a hue in hand — which is how a fifth hard-coded label table gets written.
 */
export function runeLabel(hue: RuneHue): string {
  return BY_HUE.get(hue)?.label ?? hue;
}

/**
 * The SISTER whose memory a hue is — "Vefna", "Skugga" — or the raw hue string
 * if it is not one of the eight.
 *
 * The same argument `runeLabel` makes, for the other name in the table. §450's
 * hover card over a memory source has to say WHOSE memory a stone holds, and
 * the alternative was an eight-row map in `source-hover.ts` — the fifth
 * hard-coded copy this accessor exists to prevent. `.sister` has been sitting in
 * `RUNES` since the palette was written; it simply had no way in.
 */
export function runeSister(hue: RuneHue): string {
  return BY_HUE.get(hue)?.sister ?? hue;
}

/**
 * Thread-colour as normalized RGB, for the 3D side (emissive colours, particle
 * tints). Same single source as the DOM panels — before this existed, the same
 * palette was hand-typed as floats in `threads-of-fate.ts` and `renderer.ts`,
 * as hex in the two panels, and as `Color` objects in `anchor-glow.ts`.
 *
 * Kept as plain maths so this module never needs `three`; the DOM panels import
 * it too.
 */
function hueRgb(hue: RuneHue): [number, number, number] {
  const hex = hueColor(hue);
  const n = Number.parseInt(hex.slice(1), 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

/**
 * Thread-colour as EMISSIVE light — for additive threads, glowing rune-stones
 * and anything where the colour IS the light rather than ink on a page.
 *
 * Same hue, different medium. The panel colours are mixed to sit on warm
 * parchment, so several are deliberately dark — Skugga's forest-green most of
 * all. Fed straight into an emissive or additive material those would read
 * 16–34 % dimmer than the palette they replaced, which is a lighting regression
 * wearing a lore fix's clothes.
 *
 * So the channels are scaled up until the brightest hits 1.0. That preserves the
 * hue and saturation EXACTLY (it is a pure scalar multiply) while giving every
 * Sister the same full-strength presence in the world. Intensity stays the
 * caller's business, as it already was.
 */
export function hueGlow(hue: RuneHue): [number, number, number] {
  const [r, g, b] = hueRgb(hue);
  const peak = Math.max(r, g, b);
  if (peak <= 0) return [r, g, b];
  return [r / peak, g / peak, b / peak];
}

/** URL of the glyph, for `mask-image` (so it takes the thread-colour). */
export function hueGlyphUrl(hue: RuneHue): string {
  return `/ui/runes/rune-${BY_HUE.get(hue)?.glyph ?? "sol"}.svg`;
}

/**
 * Where a rune sits on the star, as a unit offset from the centre with +y DOWN
 * (CSS screen space). Crown is (0, −1).
 */
export function runeOffset(meta: RuneMeta): { x: number; y: number } {
  const rad = (meta.angle * Math.PI) / 180;
  return { x: Math.sin(rad), y: -Math.cos(rad) };
}
